/**
 * Convert RFQ front-end data to back-end data
 */

import cloneDeep from 'lodash/cloneDeep';

import { MultiselectProps } from '@amzn/awsui-components-react-v3';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';

import { CommonConstants, RequestPropertyIds } from '../../../../data/BackendEntityConstants';
import {
  DCSP,
  getRFXLineItemClassId,
  getRFXRequestClassId,
  getRFXResponseClassId,
  shouldNotSkipOfferGeneration
} from '../../../../data/BusinessConstants';
import {
  Document,
  Property,
  RFXCommodityInformation,
  RFXIdentifier,
  RFXRequest
} from '../../../../interfaces/RFXBackendModel';
import {
  CommodityInformation,
  DCSPEventSubTypes,
  DCSPRFXRequestInformation,
  RFXRequestBaseInformation,
  RFXRequestForm,
  RFXRequestGeneralInformation
} from '../../../../interfaces/RFXRequestForm';
import { getBusinessId, shouldSupportMultipleRegions } from '../../../BusinessUtils';
import { convertCoManagerEntriesToProperty } from '../../../CoManagerUtils';
import { createProperty } from '../../../RFXUtils';
import { getWorkflowFromGeneraInfo } from '../../../WorkflowUtils';
import {
  convertQuoteEntriesToDocument,
  convertRFXRequestDocumentsToBackendModel,
  convertSupplierEntries,
  convertTrackingInfoToProperties
} from '../../common/backendConverter';
import { convertCategoryOptions, convertRegionOptions } from './backendHelper';
import { isValidEventSubtype } from './commonHelper';

/**
 * Converts/updates frontend model RFXRequestForm to backend model RFXRequest
 * @param rfxRequest RFXRequest
 * @param rfxRequestForm RFXRequestForm
 */
export function convertRFXRequestFormToRFXReqBackendModel(
  rfxRequest: RFXRequest,
  rfxRequestForm: RFXRequestForm,
  username: string
): RFXRequest {
  if (
    rfxRequest.rfxRequestRevisionedId.id.value &&
    rfxRequest.rfxRequestRevisionedId.id.value !== rfxRequestForm.generalInformation.fullRfxId
  ) {
    // TODO: use argo logger
    console.error('RFXRequest and RFXRequestForm do not have the same RFX ID. Returning original RFXRequest');
  }
  let rfxRequestBackendModel: RFXRequest = cloneDeep(rfxRequest);
  if (!rfxRequestBackendModel.ingestionId) {
    rfxRequestBackendModel.ingestionId = undefined;
  }

  const businessId: string = getBusinessId(rfxRequestForm.generalInformation);
  const workflow: string = getWorkflowFromGeneraInfo(rfxRequestForm.generalInformation);
  const rfxType = rfxRequestForm.generalInformation.rfxType;

  rfxRequestBackendModel.rfxRequestClassId.value = getRFXRequestClassId(businessId, workflow);
  rfxRequestBackendModel.rfxResponseClassId.value = getRFXResponseClassId(businessId, workflow);
  rfxRequestBackendModel.rfxLineItemClassId.value = getRFXLineItemClassId(
    businessId,
    workflow,
    rfxRequestForm.generalInformation.quoteTemplateType?.label
  );

  rfxRequestBackendModel.commodityInformation = convertCommodityInformationFrontendModelToBackendModel(
    rfxRequestForm.quotes.commodityInformation
  );

  rfxRequestBackendModel.rfxRequestRevisionedId.id.value = rfxRequestForm.generalInformation.fullRfxId;
  rfxRequestBackendModel.rfxRequestRevisionedId.revision = rfxRequestForm.round;
  rfxRequestBackendModel.rfxRequestVersion = rfxRequestForm.rfxRequestVersion;
  if (businessId) {
    rfxRequestBackendModel.businessId = { value: businessId };
  }

  if (shouldSupportMultipleRegions(rfxType, businessId, workflow)) {
    rfxRequestBackendModel.region.value = CommonConstants.UNSET_REGION_VALUE;
  } else {
    const region = rfxRequestForm.generalInformation.region.label!;
    if (region === '') {
      throw new Error('Region is required');
    }
    rfxRequestBackendModel.region.value = region;
  }

  const beginDate = rfxRequestForm.generalInformation.beginDate.trim();
  if (beginDate === '') {
    throw new Error('Begin date is required.');
  }
  const beginTime = rfxRequestForm.generalInformation.beginTime.trim();
  if (beginTime === '') {
    throw new Error('Begin time is required.');
  }
  const beginAtValue = Date.parse(`${beginDate}T${beginTime}`);
  rfxRequestBackendModel.beginAt = beginAtValue;

  const dueDate = rfxRequestForm.generalInformation.dueDate.trim();
  if (dueDate === '') {
    throw new Error('Deadline for supplier is required.');
  }
  const dueTime = rfxRequestForm.generalInformation.dueTime.trim();
  if (dueTime === '') {
    throw new Error('Deadline time for supplier is required.');
  }
  const dueAtValue = Date.parse(`${dueDate}T${dueTime}`);
  if (dueAtValue < beginAtValue) {
    throw new Error('Deadline for supplier must be later than begin date.');
  }
  rfxRequestBackendModel.dueAt = dueAtValue;

  const name = rfxRequestForm.generalInformation.rfxName.trim();
  if (name === '') {
    throw new Error('RFX name is required.');
  }
  rfxRequestBackendModel.name = name;

  rfxRequestBackendModel.type = rfxRequestForm.generalInformation.rfxType;
  if (!rfxRequestBackendModel.buyer.value || rfxRequestBackendModel.buyer.value.length === 0) {
    rfxRequestBackendModel.buyer.value = username;
  }
  rfxRequestBackendModel.currency = rfxRequestForm.generalInformation.currency.label!;
  rfxRequestBackendModel.suppliers = convertSupplierEntries(rfxRequestForm.suppliers.supplierEntries, businessId, workflow);
  let properties: Property[] = [];
  properties.push(...convertGeneralInfoToProperty(rfxRequestForm.generalInformation));
  properties.push(...convertTrackingInfoToProperties(rfxRequestForm.trackingInformation));

  if (rfxRequestForm['co-managers']?.coManagerEntries) {
    properties.push(convertCoManagerEntriesToProperty(rfxRequestForm['co-managers'].coManagerEntries));
  }
  rfxRequestBackendModel.properties = properties;

  rfxRequestBackendModel.documents = [];

  let quoteFile: Document | null = convertQuoteEntriesToDocument(rfxRequestForm.quotes.quoteEntries);
  if (quoteFile !== null) {
    rfxRequestBackendModel.documents.push(quoteFile);
  }

  let documents = convertRFXRequestDocumentsToBackendModel(rfxRequestForm.documents);
  for (let i = 0; i < documents.length; ++i) {
    rfxRequestBackendModel.documents.push(documents[i]);
  }

  rfxRequestBackendModel.generalInformation = {
    overview: rfxRequestForm.generalInformation.overview,
    processDetail: rfxRequestForm.generalInformation.processDetails
  };

  rfxRequestBackendModel.lifeCycle.lifeCycleState.name = rfxRequestForm.state;

  return rfxRequestBackendModel;
}

const convertDCSPSites = (siteOptions: MultiselectProps.Options | undefined, properties: Property[]) => {
  let sites: string[] = [];
  if (siteOptions !== undefined) {
    sites = siteOptions
      .filter((siteOption: OptionDefinition) => siteOption.label !== '')
      .map((siteOption: OptionDefinition) => siteOption.label!);
    properties.push(createProperty(RequestPropertyIds.Sites, JSON.stringify(sites)));
  }
};

/**
 * Convert general information to back-end properties
 * @param generalInfo general information from UI
 * @returns converted properties that can be used for back-end
 */
const convertGeneralInfoToProperty = (generalInfo: RFXRequestGeneralInformation): Property[] => {
  const businessId = getBusinessId(generalInfo);
  const workflow = getWorkflowFromGeneraInfo(generalInfo);
  switch (businessId) {
    case DCSP:
      if (workflow === DCSP) {
        return convertDCSPGeneralInfoToProperty(generalInfo);
      }
      break;
    default:
      break;
  }
  return convertDefaultGeneralInfoToProperty(generalInfo);
};

/**
 * Convert general information to back-end properties for DCSP
 */
export function convertDCSPGeneralInfoToProperty(generalInfoInput: RFXRequestGeneralInformation): Property[] {
  const properties: Property[] = [];

  const generalInfo: DCSPRFXRequestInformation = generalInfoInput as DCSPRFXRequestInformation;

  if (generalInfo.workflow) {
    properties.push(createProperty(RequestPropertyIds.Workflow, generalInfo.workflow));
  }

  if (generalInfo.department.value && generalInfo.department.label !== '') {
    properties.push(createProperty(RequestPropertyIds.Department, generalInfo.department.label!));
  }

  if (generalInfo.organization.value && generalInfo.organization.label !== '') {
    properties.push(createProperty(RequestPropertyIds.Organization, generalInfo.organization.label!));
  }

  if (generalInfo.project && generalInfo.project !== '') {
    properties.push(createProperty(RequestPropertyIds.Project, generalInfo.project!));
  }

  if (
    generalInfo.needByDate &&
    generalInfo.needByDate !== '' &&
    generalInfo.needByTime &&
    generalInfo.needByTime !== ''
  ) {
    const needByDateTimestamp: number = Date.parse(`${generalInfo.needByDate}T${generalInfo.needByTime}`);
    properties.push(createProperty(RequestPropertyIds.EvaluationDate, String(needByDateTimestamp), 'Long'));
  }

  convertCategoryOptions(generalInfo, properties);
  if (generalInfo.subCategory?.label) {
    properties.push(createProperty(RequestPropertyIds.SubCategory, generalInfo.subCategory.label));
  }
  convertRegionOptions(generalInfo, properties);

  // Build ID
  if (generalInfo.isBuildIdAvailable === 'Yes') {
    if (generalInfo.buildId === undefined || generalInfo.buildId === '') {
      throw Error('Build ID is required.');
    }
    properties.push(createProperty(RequestPropertyIds.BuildId, generalInfo.buildId));
  } else if (generalInfo.isBuildIdAvailable === 'No') {
    if (generalInfo.buildIdComments === undefined || generalInfo.buildIdComments === '') {
      throw Error('Comments for build ID are required.');
    }
    properties.push(createProperty(RequestPropertyIds.BuildIdComments, generalInfo.buildIdComments));
  } else {
    throw Error('Please choose whether Rev. build ID is available!');
  }

  // Estimated amount
  if (generalInfo.estimatedAmount !== undefined && generalInfo.estimatedAmount !== '') {
    properties.push(createProperty(RequestPropertyIds.EstimatedAmount, generalInfo.estimatedAmount));
  }

  // Sites
  convertDCSPSites(generalInfo.sites, properties);

  const eventSubType = generalInfo.eventSubType;
  const option = eventSubType.option?.value;
  if (option === null || option === undefined) {
    throw Error('Event sub type is required.');
  }

  if (!isValidEventSubtype(option)) {
    throw Error('Enter a valid event sub type.');
  }

  const isAligned = eventSubType.isAligned;
  const nonAlignmentReason = eventSubType.nonAlignmentReason;
  if (option === DCSPEventSubTypes.Competitive && isAligned === 'No' && !nonAlignmentReason) {
    throw Error('Please provide reason(s) for this potential non-compliance, otherwise ensure supporting document is available before proceeding');
  }

  properties.push(createProperty(RequestPropertyIds.EventSubType, option));

  switch (option) {
    case DCSPEventSubTypes.Competitive:
      if (eventSubType.isAligned === undefined) {
        throw Error('Whether bids award criteria have been aligned with the customer is required.');
      }
      if (eventSubType.isAligned === 'Yes') {
        properties.push(createProperty(RequestPropertyIds.HasAlignmentDocument, eventSubType.isAligned));
        // Reset the NonAlignmentReason value if isAligned is changed from 'No' to 'Yes'
        properties.push(createProperty(RequestPropertyIds.NonAlignmentReason, ''));
      }
      if (eventSubType.isAligned === 'No') {
        properties.push(createProperty(RequestPropertyIds.HasAlignmentDocument, eventSubType.isAligned));
        properties.push(createProperty(RequestPropertyIds.NonAlignmentReason, eventSubType.nonAlignmentReason!));
      }
      break;
    case DCSPEventSubTypes.SSJ:
      if (eventSubType.ssjLink === undefined || eventSubType.ssjLink.trim() === '') {
        throw Error('SSJ link is required.');
      }
      properties.push(createProperty(RequestPropertyIds.SSJLink, eventSubType.ssjLink));
      break;
    case DCSPEventSubTypes.Repricing:
      if (eventSubType.repricing === undefined) {
        throw Error('Repricing information is required.');
      }
      if (eventSubType.repricing.isSourceEventIdAvailable === 'Yes') {
        properties.push(
          createProperty(RequestPropertyIds.RepricingSourceEventId, eventSubType.repricing.sourceEventId)
        );
      } else {
        properties.push(createProperty(RequestPropertyIds.RepricingComments, eventSubType.repricing.comments));
      }
      break;
    case DCSPEventSubTypes.ContractRenewal:
      if (eventSubType.renewal === undefined) {
        throw Error('Contract renewal information is required.');
      }
      if (eventSubType.renewal.isSourceEventIdAvailable === 'Yes') {
        properties.push(createProperty(RequestPropertyIds.RenewalSourceEventId, eventSubType.renewal.sourceEventId));
      } else {
        properties.push(createProperty(RequestPropertyIds.RenewalComments, eventSubType.renewal.comments));
      }
      break;
  }

  return properties;
}

/**
 * Convert base general information to back-end properties
 */
const convertBaseGeneralInfoToProperty = (generalInfo: RFXRequestBaseInformation): Property[] => {
  const businessId = getBusinessId(generalInfo);
  let properties: Property[] = [];
  if (generalInfo.workflow) {
    properties.push(createProperty(RequestPropertyIds.Workflow, generalInfo.workflow));
  }

  if (generalInfo.department.value && generalInfo.department.label !== '') {
    properties.push(createProperty(RequestPropertyIds.Department, generalInfo.department.label!));
  }

  if (generalInfo.defaultQuoteValidity !== undefined && shouldNotSkipOfferGeneration(businessId)) {
    properties.push(
      createProperty(RequestPropertyIds.DefaultQuoteValidity, generalInfo.defaultQuoteValidity.toString(), 'Int')
    );
  }

  if (generalInfo.organization.value && generalInfo.organization.label !== '') {
    properties.push(createProperty(RequestPropertyIds.Organization, generalInfo.organization.label!));
  }

  if (generalInfo.project && generalInfo.project !== '') {
    properties.push(createProperty(RequestPropertyIds.Project, generalInfo.project!));
  }

  if (
    generalInfo.needByDate &&
    generalInfo.needByDate !== '' &&
    generalInfo.needByTime &&
    generalInfo.needByTime !== ''
  ) {
    const needByDateTimestamp: number = Date.parse(`${generalInfo.needByDate}T${generalInfo.needByTime}`);
    properties.push(createProperty(RequestPropertyIds.EvaluationDate, String(needByDateTimestamp), 'Long'));
  }

  if (generalInfo.simUrl && generalInfo.simUrl !== '') {
    properties.push(createProperty(RequestPropertyIds.SimUrl, generalInfo.simUrl!));
  }

  return properties;
};

/**
 * Default implementation to convert general information to back-end properties
 */
const convertDefaultGeneralInfoToProperty = (generalInfo: RFXRequestGeneralInformation): Property[] => {
  const properties: Property[] = convertBaseGeneralInfoToProperty(generalInfo);
  convertCategoryOptions(generalInfo, properties);
  convertRegionOptions(generalInfo, properties);
  return properties;
};

// Converts the provided frontend model for commodity information to backend model.
// If frontend model is undefined, then returns undefined.
// If frontend model is valid, then convert the values to identifiers and return backend model.
export function convertCommodityInformationFrontendModelToBackendModel(
  commodityInformation: CommodityInformation | undefined
): RFXCommodityInformation | undefined {
  if (commodityInformation) {
    var rootId: RFXIdentifier = {
      value: commodityInformation.rootId
    };
    var commodityIds: RFXIdentifier[] = commodityInformation.commodityIds
      ? commodityInformation.commodityIds.map((commodityId) => {
          return { value: commodityId };
        })
      : [];
    var category: RFXIdentifier | undefined = commodityInformation.category
      ? {
          value: commodityInformation.category
        }
      : undefined;

    return { rootId: rootId, commodityIds: commodityIds, category: category };
  } else {
    return undefined;
  }
}
